/* eslint-disable max-statements */
import React, { memo, useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentToken } from '../../pages/login/authSlice';
import {
    selectFloatingRecorder,
    stopFloatingRecording,
    resetFloatingRecording,
} from './floatingRecorderSlice';
import { useLazyGetVoiceRecordingSignedUrlsQuery } from './voiceRecordingSlice';
import { RecordingEndedModal } from './RecordingEndedModal';

// ---------------------------------------------------------------------------
// Constants — mirrors recording.js
// ---------------------------------------------------------------------------

const CHUNK_INTERVAL_MS = 10_000;
const MAX_CHUNK_COUNT = 360;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const selectMimeType = (): string => {
    if (typeof MediaRecorder === 'undefined') return 'audio/webm';
    const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
    ];
    return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? 'audio/webm';
};

const MIME_TYPE = selectMimeType();

const pad = (n: number) => String(n).padStart(2, '0');

const buildFileName = (callerId: string | number, calleeId: string | number): string => {
    // Matches WebRtcDashboard.tsx exactly: setFileName(`${empId}_${CurrentChatAgtId}`)
    // The backend prefixes date-time and appends .weba when composing the chunks.
    return `${callerId}_${calleeId}`;
};

const formatTime = (totalSeconds: number): string =>
    `${pad(Math.floor(totalSeconds / 60))}:${pad(totalSeconds % 60)}`;

const bytesToSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / (1024 ** i))} ${sizes[i]}`;
};

// ---------------------------------------------------------------------------
// FloatingRecorderBar
// Mounts once in App.tsx — persists across all route navigations.
// Becomes visible when floatingRecorder.isActive === true.
// Immediately starts recording when activated.
// ---------------------------------------------------------------------------

export const FloatingRecorderBar = memo(() => {
    const dispatch = useDispatch();
    const token = useSelector(selectCurrentToken);
    const { isActive, callerId, calleeId, associateName } = useSelector(selectFloatingRecorder);

    const [fetchSignedUrls] = useLazyGetVoiceRecordingSignedUrlsQuery();
    // mark-complete uses [FromForm] — raw fetch is the only reliable way to send
    // FormData through RTK Query's fetchBaseQuery (which serialises FormData to {}).
    const apiBaseUrl = (process.env.REACT_APP_API_URI as string) ?? '';

    type Phase = 'idle' | 'initializing' | 'recording' | 'stopping' | 'done' | 'error';
    const [phase, setPhase] = useState<Phase>('idle');
    const [elapsedSec, setElapsedSec] = useState(0);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [showEndedModal, setShowEndedModal] = useState(false);

    const [, startTimerTransition] = useTransition();

    // Stable refs — safe to read inside async closures
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const startTimeRef = useRef<number>(0);
    const uploadIdRef = useRef<string>('');
    const fileNameRef = useRef<string>('');
    const signedUrlsRef = useRef<string[]>([]);
    const chunkIndexRef = useRef<number>(0);
    const pendingUploadsRef = useRef<Promise<void>[]>([]);
    const isStoppingRef = useRef(false);

    // ---------------------------------------------------------------------------
    // Cleanup helpers
    // ---------------------------------------------------------------------------

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    }, []);

    const resetRefs = useCallback(() => {
        uploadIdRef.current = '';
        fileNameRef.current = '';
        signedUrlsRef.current = [];
        chunkIndexRef.current = 0;
        pendingUploadsRef.current = [];
        isStoppingRef.current = false;
    }, []);

    // ---------------------------------------------------------------------------
    // GCS direct chunk upload — identical to recording.js
    // ---------------------------------------------------------------------------

    const uploadChunkDirectly = useCallback(
        async (blob: Blob, chunkNumber: number, signedUrl: string): Promise<void> => {
            try {
                console.log(`[FloatingBar] Uploading chunk ${chunkNumber} (${bytesToSize(blob.size)})…`);
                const response = await fetch(signedUrl, {
                    method: 'PUT',
                    body: blob,
                    headers: { 'Content-Type': 'audio/webm' },
                });
                if (!response.ok) throw new Error(`GCS PUT failed: HTTP ${response.status}`);
                console.log(`[FloatingBar] ✅ Chunk ${chunkNumber} uploaded`);
            } catch (err) {
                console.error(`[FloatingBar] ❌ Chunk ${chunkNumber} failed:`, err);
            }
        },
        []
    );

    // ---------------------------------------------------------------------------
    // mark-complete — raw fetch to preserve FormData boundary
    // ---------------------------------------------------------------------------

    const sendCompletionMarker = useCallback(
        async (uploadId: string, fileName: string, chunkCount: number): Promise<void> => {
            try {
                const formData = new FormData();
                formData.append('uploadId', uploadId);
                formData.append('fileName', fileName);
                formData.append('chunkCount', String(chunkCount));

                const response = await fetch(`${apiBaseUrl}upload/mark-complete`, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(`HTTP ${response.status}: ${text}`);
                }

                console.log('[FloatingBar] ✅ Completion marker sent');
            } catch (err) {
                console.error('[FloatingBar] markComplete failed, trying sendBeacon:', err);
                if (navigator.sendBeacon) {
                    const fd = new FormData();
                    fd.append('uploadId', uploadId);
                    fd.append('fileName', fileName);
                    fd.append('chunkCount', String(chunkCount));
                    navigator.sendBeacon(`${apiBaseUrl}upload/mark-complete`, fd);
                    console.log('[FloatingBar] ✅ sendBeacon fallback fired');
                }
            }
        },
        [apiBaseUrl]
    );

    // ---------------------------------------------------------------------------
    // Stable stop helper — safe to call from ondataavailable closure
    // ---------------------------------------------------------------------------

    const stopRecorderIfActive = useCallback(() => {
        if (isStoppingRef.current) return;
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== 'inactive') {
            isStoppingRef.current = true;
            clearTimer();
            mr.stop();
        }
    }, [clearTimer]);

    // ---------------------------------------------------------------------------
    // Start recording — triggered automatically when isActive flips to true
    // ---------------------------------------------------------------------------

    const startRecording = useCallback(async () => {
        if (!token) {
            setErrorMsg('No auth token — please log in again.');
            setPhase('error');
            return;
        }

        setPhase('initializing');
        setErrorMsg(null);
        setElapsedSec(0);

        // 1. Mic
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            setErrorMsg('Microphone access denied.');
            setPhase('error');
            dispatch(stopFloatingRecording());
            return;
        }
        streamRef.current = stream;

        // 2. Signed URLs
        const fileName = buildFileName(callerId, calleeId);
        console.log(`[FloatingBar] 📄 fileName: ${fileName}`);

        let signedUrls: string[] = [];
        let uploadId = '';

        try {
            const result = await fetchSignedUrls({ chunkCount: MAX_CHUNK_COUNT, fileName }).unwrap();
            if (!result.success || !result.signedUrls?.length) {
                throw new Error('Invalid server response');
            }
            signedUrls = result.signedUrls;
            uploadId = result.uploadId;
        } catch (err: any) {
            stopStream();
            setErrorMsg(`Init failed: ${err?.message ?? 'Unknown error'}`);
            setPhase('error');
            dispatch(stopFloatingRecording());
            return;
        }

        signedUrlsRef.current = signedUrls;
        uploadIdRef.current = uploadId;
        fileNameRef.current = fileName;
        chunkIndexRef.current = 0;
        pendingUploadsRef.current = [];

        console.log(`[FloatingBar] ✅ ${signedUrls.length} signed URLs — uploadId: ${uploadId}`);

        // 3. MediaRecorder
        let recorder: MediaRecorder;
        try {
            recorder = new MediaRecorder(stream, { mimeType: MIME_TYPE });
        } catch (err) {
            stopStream();
            setErrorMsg(`MediaRecorder error: ${String(err)}`);
            setPhase('error');
            dispatch(stopFloatingRecording());
            return;
        }
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e: BlobEvent) => {
            if (e.data && e.data.size > 0) {
                if (chunkIndexRef.current >= signedUrlsRef.current.length) {
                    console.error('[FloatingBar] ⚠️ Ran out of signed URLs — stopping.');
                    stopRecorderIfActive();
                    return;
                }
                const chunkNumber = chunkIndexRef.current;
                const url = signedUrlsRef.current[chunkNumber];
                chunkIndexRef.current++;
                pendingUploadsRef.current.push(uploadChunkDirectly(e.data, chunkNumber, url));
            }
        };

        recorder.onstop = async () => {
            setPhase('stopping');

            // Capture refs into locals BEFORE resetRefs() wipes them
            const capturedUploadId = uploadIdRef.current;
            const capturedFileName = fileNameRef.current;

            console.log(`[FloatingBar] ⏳ Waiting for ${pendingUploadsRef.current.length} uploads…`);
            await Promise.all(pendingUploadsRef.current);
            console.log('[FloatingBar] ✅ All chunks complete');

            const finalChunkCount = chunkIndexRef.current;
            console.log(`[FloatingBar] mark-complete → uploadId: "${capturedUploadId}" fileName: "${capturedFileName}" chunkCount: ${finalChunkCount}`);

            await sendCompletionMarker(capturedUploadId, capturedFileName, finalChunkCount);

            stopStream();
            clearTimer();
            resetRefs();
            mediaRecorderRef.current = null;
            setPhase('done');
            setElapsedSec(0);

            // Tell Redux the session is over AFTER all cleanup
            dispatch(stopFloatingRecording());

            setShowEndedModal(true);
        };

        // 4. Start with 10-second chunks
        recorder.start(CHUNK_INTERVAL_MS);
        startTimeRef.current = Date.now();

        timerRef.current = setInterval(() => {
            startTimerTransition(() => {
                setElapsedSec(Math.floor((Date.now() - startTimeRef.current) / 1000));
            });
        }, 1000);

        setPhase('recording');
    }, [
        token,
        callerId,
        calleeId,
        fetchSignedUrls,
        stopStream,
        uploadChunkDirectly,
        sendCompletionMarker,
        clearTimer,
        resetRefs,
        startTimerTransition,
        stopRecorderIfActive,
        dispatch,
    ]);

    // ---------------------------------------------------------------------------
    // "End Recording" button — stop recorder only; Redux updated in onstop
    // ---------------------------------------------------------------------------

    const handleEndRecording = useCallback(() => {
        stopRecorderIfActive();
    }, [stopRecorderIfActive]);

    // ---------------------------------------------------------------------------
    // Auto-start when Redux activates the bar
    // ---------------------------------------------------------------------------

    useEffect(() => {
        if (isActive && phase === 'idle') {
            startRecording();
        }
    }, [isActive, startRecording]);

    // Teardown — only for error/abandon paths (onstop handles normal stop)
    useEffect(() => {
        if (!isActive && phase !== 'idle' && phase !== 'stopping' && phase !== 'done') {
            clearTimer();
            stopStream();
            resetRefs();
            mediaRecorderRef.current = null;
            setPhase('idle');
            setElapsedSec(0);
        }
    }, [isActive, phase, clearTimer, stopStream, resetRefs]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearTimer();
            stopStream();
        };
    }, [clearTimer, stopStream]);

    // ---------------------------------------------------------------------------
    // "Recording Ended" modal close
    // ---------------------------------------------------------------------------

    const handleEndedModalClose = useCallback(() => {
        setShowEndedModal(false);
        setPhase('idle');
        dispatch(resetFloatingRecording());
    }, [dispatch]);

    // ---------------------------------------------------------------------------
    // Derived display values
    // ---------------------------------------------------------------------------

    const isRecording = phase === 'recording';
    const isInitializing = phase === 'initializing';
    const isStopping = phase === 'stopping';
    const timerLabel = formatTime(elapsedSec);
    const barVisible = isActive || isStopping || isInitializing;

    if (!barVisible && !showEndedModal) return null;

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    return (
        <>
            {barVisible && (
                <div
                    role="region"
                    aria-label="Voice recording controls"
                    style={{
                        position: 'fixed',
                        top: '16px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9000,
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #2d4a5a 0%, #1e3a4a 100%)',
                        borderRadius: '50px',
                        padding: '10px 28px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                        userSelect: 'none',
                    }}
                >
                    {/* Recording indicator */}
                    <button
                        aria-label={isInitializing ? 'Initializing…' : `Recording — ${timerLabel}`}
                        title={associateName ? `Recording with ${associateName}` : 'Recording in progress'}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'default',
                            padding: '0 20px 0 0',
                        }}
                    >
                        <span
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#5b4fd4',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                            }}
                            aria-hidden="true"
                        >
                            <span
                                style={{
                                    position: 'absolute',
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    border: '2.5px solid rgba(255,255,255,0.55)',
                                    animation: isRecording ? 'vrbar-pulse-ring 1.4s ease-in-out infinite' : 'none',
                                }}
                            />
                            <span
                                style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    border: '2.5px solid #fff',
                                    background: 'transparent',
                                    display: 'block',
                                }}
                            />
                        </span>
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 600, letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                            {(() => {
                                if (isInitializing) return 'Initializing…';
                                if (isStopping) return 'Saving…';
                                return `Recording ${timerLabel}`;
                            })()}
                        </span>
                    </button>

                    {/* Divider */}
                    <span
                        style={{ width: '1px', height: '44px', background: 'rgba(255,255,255,0.18)', flexShrink: 0 }}
                        aria-hidden="true"
                    />

                    {/* End Recording */}
                    <button
                        onClick={handleEndRecording}
                        disabled={isStopping || isInitializing}
                        aria-label="End Recording"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'transparent',
                            border: 'none',
                            cursor: isStopping || isInitializing ? 'not-allowed' : 'pointer',
                            padding: '0 0 0 20px',
                            opacity: isStopping || isInitializing ? 0.5 : 1,
                            transition: 'opacity 0.2s',
                        }}
                    >
                        <span
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#ef4444',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            aria-hidden="true"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M2 2L14 14M14 2L2 14" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                            </svg>
                        </span>
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 600, letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                            End Recording
                        </span>
                    </button>
                </div>
            )}

            {/* Error toast */}
            {errorMsg && isActive && (
                <div
                    role="alert"
                    style={{
                        position: 'fixed',
                        top: '90px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9001,
                        background: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        color: '#92400e',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                >
                    <i className="pi pi-exclamation-triangle" style={{ marginRight: '6px' }} />
                    {errorMsg}
                </div>
            )}

            <RecordingEndedModal visible={showEndedModal} onClose={handleEndedModalClose} />

            <style>{`
                @keyframes vrbar-pulse-ring {
                    0%, 100% { transform: scale(1); opacity: 0.55; }
                    50%       { transform: scale(1.15); opacity: 0.9; }
                }
            `}</style>
        </>
    );
});

FloatingRecorderBar.displayName = 'FloatingRecorderBar';
